import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, Backdrop, CircularProgress } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import AdvertisementForm from "../../component/admin/formComponents/AdvertisementForm";
import { getAdvertisement } from "../../api/getAdvertisement";

const optionalUrlSchema = Yup.string()
  .trim()
  .test(
    "optional-url",
    "Enter a valid URL (http:// or https://)",
    (value) => {
      if (!value) return true;
      try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
      } catch {
        return false;
      }
    }
  );

export default function EditAdvertisement() {
  const token = useSelector((state) => state.auth.token);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams();

  const formik = useFormik({
    initialValues: {
      link: "",
      image: "",
    },
    validationSchema: Yup.object().shape({
      link: optionalUrlSchema,
      image: Yup.mixed().required("Banner image is required"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      const data = new FormData();
      data.append("id", id);
      data.append("link", values.link?.trim() || "");
      if (values.image instanceof File) {
        data.append("image", values.image);
      }
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/editAdvertisement`,
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.success) {
          toast.success(response.data.message);
          setTimeout(() => {
            navigate("/admin/dashboard/advertisements");
            setLoading(false);
          }, 500);
        } else {
          toast.error(response.data.message);
          setLoading(false);
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed to update advertisement");
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        toast.error("Advertisement ID is missing");
        navigate("/admin/dashboard/advertisements");
        return;
      }
      try {
        const data = await getAdvertisement(id, token);
        if (data.success) {
          formik.setValues({
            link: data.data.link || "",
            image: data.data.banner_image || "",
          });
        } else {
          toast.error(data.message);
        }
      } catch (e) {
        toast.error("Error while fetching advertisement");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <>
      <ToastContainer />
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress sx={{ color: "#ef364e" }} />
      </Backdrop>
      <Box className="free_votes_page">
        <Box className="page_title">
          <Typography variant="h1" component="h1">
            Edit Advertisement
          </Typography>
        </Box>
        <AdvertisementForm formik={formik} edited={true} />
      </Box>
    </>
  );
}
